import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MailIcon, UsersIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SearchInput,
  Tabs,
  SkeletonTable,
  type DataTableHeader,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  NEW_TEMPLATE,
  OUTREACH_QUERY_KEYS,
  OUTREACH_TEMPLATE_SCOPE,
  copyTeamTemplate,
  listMyTemplates,
  listTeamTemplates,
  outreachTemplatePath,
  type OutreachTemplate,
} from '@/lib/outreach-templates';

const SORT_FIELDS = { name: 'name', subject: 'subject', updated: 'updatedAt' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.name, direction: 'asc' };
/** The team table keeps its own sort in the URL. */
const TEAM_SORT_PREFIX = 'team';

/** Your templates and the team's, one tab each. */
const TAB_PARAM = 'tab';
const TEMPLATE_TAB = { mine: 'mine', team: 'team' } as const;
type TemplateTab = (typeof TEMPLATE_TAB)[keyof typeof TEMPLATE_TAB];

const HEADERS: readonly DataTableHeader[] = [
  { label: 'Template', sortKey: SORT_FIELDS.name },
  { label: 'Subject', sortKey: SORT_FIELDS.subject },
  { label: 'Updated', sortKey: SORT_FIELDS.updated },
];
const TEAM_HEADERS: readonly DataTableHeader[] = [...HEADERS, 'Actions'];
const TEAM_ACTIONS_COLUMN = TEAM_HEADERS.length - 1;

/** `2026-10-24T12:00:00Z` → "Oct 24, 2026". */
function readableDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function matches(template: OutreachTemplate, needle: string): boolean {
  return needle === '' || [template.name, template.whenToUse, template.subject, template.body].join(' ').toLowerCase().includes(needle);
}

/**
 * Sales → My email templates (repeat clients, Phase C): the rep's own
 * outreach templates and the team's templates
 * they can use or copy. Drafts for the rep's clients start from these; the
 * page itself never sends anything.
 *
 * @returns The page.
 */
export function OutreachTemplatesPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: TemplateTab = params.get(TAB_PARAM) === TEMPLATE_TAB.team ? TEMPLATE_TAB.team : TEMPLATE_TAB.mine;
  const { search, setSearch, debounced } = useUrlSearch();
  const mineSort = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const teamSort = useTableSort(SORT_KEYS, DEFAULT_SORT, { prefix: TEAM_SORT_PREFIX });

  const mine = useQuery({ queryKey: OUTREACH_QUERY_KEYS.mine, queryFn: ({ signal }) => listMyTemplates(signal) });
  const team = useQuery({ queryKey: OUTREACH_QUERY_KEYS.team, queryFn: ({ signal }) => listTeamTemplates(signal) });

  const copy = useMutation({
    mutationFn: (template: OutreachTemplate) => copyTeamTemplate(template.id),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: OUTREACH_QUERY_KEYS.mine });
      // Open the copy, so making it theirs is the next thing the rep does.
      navigate(outreachTemplatePath(OUTREACH_TEMPLATE_SCOPE.personal, created.id));
    },
  });

  const needle = debounced.toLowerCase();
  const myRows = sortRows(
    (mine.data ?? []).filter((template) => matches(template, needle)),
    mineSort.sort ?? DEFAULT_SORT,
  );
  const teamRows = sortRows(
    (team.data?.templates ?? []).filter((template) => matches(template, needle)),
    teamSort.sort ?? DEFAULT_SORT,
  );
  const canManageTeam = team.data?.canManage === true;
  const newPersonal = outreachTemplatePath(OUTREACH_TEMPLATE_SCOPE.personal, NEW_TEMPLATE);
  const newTeam = outreachTemplatePath(OUTREACH_TEMPLATE_SCOPE.team, NEW_TEMPLATE);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="My email templates"
        subtitle="The emails you send past clients, in your words. Drafts for your repeat clients start from these, with the client’s details filled in."
      />

      <div className="mb-4">
        <Tabs
          label="Whose templates"
          active={tab}
          onChange={(next) =>
            setParams((previous) => {
              const updated = new URLSearchParams(previous);
              if (next === TEMPLATE_TAB.mine) updated.delete(TAB_PARAM);
              else updated.set(TAB_PARAM, next);
              return updated;
            })
          }
          items={[
            { key: TEMPLATE_TAB.mine, label: 'Your templates', ...(mine.data ? { count: mine.data.length } : {}) },
            { key: TEMPLATE_TAB.team, label: 'Team templates', ...(team.data ? { count: team.data.templates.length } : {}) },
          ]}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          label={tab === TEMPLATE_TAB.team ? 'Search team templates' : 'Search your templates'}
          placeholder="A name, a subject, a word from the email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <span className="text-portal-micro text-portal-muted">
          {tab === TEMPLATE_TAB.team
            ? 'Written by your managers. Use one as it is, or copy it to make it yours.'
            : 'Only you see these. Edit one any time; the next draft uses the change.'}
        </span>
        <div className="ml-auto">
          {tab === TEMPLATE_TAB.mine ? (
            <Button to={newPersonal}>Add template</Button>
          ) : canManageTeam ? (
            <Button to={newTeam}>Add team template</Button>
          ) : null}
        </div>
      </div>

      <div>
        <div className="min-w-0 space-y-8">
          {tab === TEMPLATE_TAB.mine ? (
          <div>
            {mine.isError ? (
              <ErrorNotice title="Couldn’t load your templates" message={errorMessage(mine.error)} />
            ) : mine.isPending ? (
              <SkeletonTable rows={3} columns={['w-[40%]', 'w-[40%]', 'w-[20%]']} label="Loading your templates…" />
            ) : myRows.length === 0 ? (
              <Panel>
                {(mine.data ?? []).length === 0 ? (
                  <EmptyState
                    icon={<MailIcon aria-hidden className="size-6" />}
                    title="No templates yet"
                    message="Write the email you'd send a happy client from last year, one for a client who asked but didn't book, and so on. Drafts pick the one that fits."
                    action={<Button to={newPersonal}>Write your first template</Button>}
                  />
                ) : (
                  <EmptyState title="No templates match" message="Try a different search." />
                )}
              </Panel>
            ) : (
              <DataTable headers={HEADERS} {...(mineSort.sort ? { sort: mineSort.sort } : {})} onSort={mineSort.onSort} minWidth={640}>
                {myRows.map((template) => (
                  <tr key={template.id} className="border-t border-portal-line align-middle">
                    <TemplateCell template={template} />
                    <td className="p-3 text-portal-small text-portal-muted">{template.subject}</td>
                    <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDate(template.updatedAt)}</td>
                  </tr>
                ))}
              </DataTable>
            )}
          </div>
          ) : (
          <div>
            {copy.isError ? (
              <div className="mb-3">
                <ErrorNotice inline title="Template not copied" message={errorMessage(copy.error)} />
              </div>
            ) : null}
            {team.isError ? (
              <ErrorNotice title="Couldn’t load team templates" message={errorMessage(team.error)} />
            ) : team.isPending ? (
              <SkeletonTable rows={2} columns={['w-[35%]', 'w-[35%]', 'w-[15%]', 'w-[15%]']} label="Loading team templates…" />
            ) : teamRows.length === 0 ? (
              <Panel>
                {team.data.templates.length === 0 ? (
                  <EmptyState
                    icon={<UsersIcon aria-hidden className="size-6" />}
                    title="No team templates yet"
                    message={
                      canManageTeam
                        ? 'Write a template every rep can use or copy, like your best email to a client who booked last year.'
                        : 'When a manager shares a template with the team, it shows here for you to use or copy.'
                    }
                    {...(canManageTeam
                      ? {
                          action: (
                            <Button to={newTeam}>Add team template</Button>
                          ),
                        }
                      : {})}
                  />
                ) : (
                  <EmptyState title="No team templates match" message="Try a different search." />
                )}
              </Panel>
            ) : (
              <DataTable
                headers={TEAM_HEADERS}
                visuallyHiddenHeaders={[TEAM_ACTIONS_COLUMN]}
                {...(teamSort.sort ? { sort: teamSort.sort } : {})}
                onSort={teamSort.onSort}
                minWidth={760}
              >
                {teamRows.map((template) => (
                  <tr key={template.id} className="border-t border-portal-line align-middle">
                    <TemplateCell template={template} />
                    <td className="p-3 text-portal-small text-portal-muted">{template.subject}</td>
                    <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDate(template.updatedAt)}</td>
                    <td className="p-3 text-portal-small">
                      <span className="flex justify-end gap-2">
                        <Button variant="secondary" size="compact" aria-label={`Use ${template.name}`} to={outreachTemplatePath(template.scope, template.id)}>
                          Use
                        </Button>
                        <Button
                          variant="secondary"
                          size="compact"
                          aria-label={`Copy ${template.name} to mine`}
                          disabled={copy.isPending}
                          onClick={() => copy.mutate(template)}
                        >
                          Copy to mine
                        </Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </div>
          )}
        </div>
      </div>

    </section>
  );
}

/** A template's name over its "when to use it" line; the name opens its page. */
function TemplateCell({ template }: { readonly template: OutreachTemplate }): React.ReactElement {
  return (
    <td className="p-3 text-portal-small">
      <Link to={outreachTemplatePath(template.scope, template.id)} className="font-bold text-brand underline-offset-2 hover:underline">
        {template.name}
      </Link>
      {template.whenToUse ? <span className="block text-portal-micro text-portal-muted">{template.whenToUse}</span> : null}
    </td>
  );
}
